import React, { useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Layout, PageHeader, Button, Form, Select, Input, Space, Spin, message, Divider, Affix } from 'antd'
import { MinusCircleOutlined } from '@ant-design/icons'

import HeaderNavigation from '../../components/HeaderNavigation'
import { currentApplicationSelector, setApplicationItem, setApplication } from '../../store/application'
import { createApplication, updateApplicationItem } from '../../services/application-items'

import { userSelector } from '../../store/session'
import { applicationSelector } from '../../store/application'
import EstimateItemInput from '../../components/estimateItem/EstimateItemInput'
import useCategory from '../../components/hooks/useCategory'
import SISelect from '../../components/SISelect'

import styles from './style.module.css'
import DecimalInput from '../../components/DecimalInput'
import { APP_STATUS_DRAFT } from '../../components/reports/constants'
import { OFF_BUDGET_OPTIONS } from '../../components/application/app-view-utils'
import WebSearchPanel from '../../components/WebSearch/WebSearchPanel'
import { ProtectedAccess } from '../../components/RoleBasedAccess'

const { Content } = Layout

const NewApplication = () => {
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  let navigate = useNavigate()
  const currentApplication = useSelector(currentApplicationSelector)
  const { currentApplicationItem, currentImplementationId } = useSelector(applicationSelector)
  const { user } = useSelector(userSelector)
  const [isCreating, setIsCreating] = useState(false)
  const project = useMemo(
    () => (currentApplicationItem ? currentApplicationItem?.project?.id : searchParams.get('project')),
    [searchParams, currentApplicationItem]
  )

  const isUpdate = searchParams.get('update')
  const [categories] = useCategory(project)
  const initFields = useMemo(
    () =>
      currentApplicationItem
        ? {
            applicationItems: [
              {
                id: currentApplicationItem.id,
                name: currentApplicationItem.name,
                estimate: {
                  key: currentApplicationItem?.estimate_field?.id,
                  value: currentApplicationItem?.estimate_field?.name,
                },
                price: currentApplicationItem?.estimate_field?.price,
                category: currentApplicationItem.category,
                amount: currentApplicationItem.amount,
                unit: currentApplicationItem?.unit,
                description: currentApplicationItem.description,
                off_budget: currentApplicationItem.off_budget,
              },
            ],
          }
        : {},
    [currentApplicationItem]
  )

  const onCancel = () => {
    form.resetFields()
    let route
    if (isUpdate === 'true' && currentImplementationId) {
      route = `/implementation?id=${currentImplementationId}`
    } else {
      route = searchParams.get('state') ? `/applications?state=${searchParams.get('state')}` : '/applications'
    }
    navigate(route, { replace: true })
    dispatch(setApplicationItem({ applicationItem: null, implementationId: null }))
  }

  const onFinish = (values) => {
    setIsCreating(true)
    if (currentApplicationItem) {
      const item = values?.applicationItems[0]
      const data = {
        data: {
          name: item?.name,
          category: item?.category ?? null,
          unit: item?.unit,
          description: item?.description,
          amount: item?.amount,
          off_budget: item?.off_budget,
          estimate_field: item?.estimate?.key ? item?.estimate?.key : null,
        },
      }
      updateApplicationItem(currentApplicationItem.id, data)
        .then((response) => {
          updateCurrentApplication(response?.data)
          message.success('Заявка успешно обновлена')
          onCancel()
        })
        .catch((error) => message.error('Ошибка при обновлении заявки'))
        .finally(() => setIsCreating(false))
    } else {
      const data = {
        data: {
          project,
          user: user.id,
          current_state: APP_STATUS_DRAFT,
        },
        items: values.applicationItems.map((item) => ({
          id: item.id,
          name: item.name,
          unit: item.unit,
          category: item.category,
          description: item.description,
          amount: item.amount,
          off_budget: item.off_budget,
          estimate_field: item.estimate?.key ? item.estimate?.key : item.estimate,
          approved: false,
        })),
      }
      createApplication(data)
        .then((response) => {
          message.success('Заявка успешно создана')
          onCancel()
        })
        .catch((error) => message.error('Ошибка при создании заявки'))
        .finally(() => setIsCreating(false))
    }
  }

  const updateCurrentApplication = (response) => {
    const application = {
      ...currentApplication,
      application_items: currentApplication.application_items.map((item) =>
        item.id === response.id ? response : item
      ),
    }
    dispatch(setApplicationItem({ applicationItem: response, implementationId: currentImplementationId }))
    dispatch(setApplication({ application }))
  }

  const onEstimateSelect = (estimate, key) => {
    const fields = form.getFieldsValue()
    if (fields && fields?.applicationItems[key]) {
      if (isUpdate !== 'true') {
        fields.applicationItems[key].amount = estimate.amount
        fields.applicationItems[key].unit = estimate.unit
        fields.applicationItems[key].name = estimate.name
        fields.applicationItems[key].price = estimate.price
      }
      fields.applicationItems[key].category = estimate.category
      fields.applicationItems[key].off_budget = null
      form.setFieldsValue(fields)
    }
  }

  const onBudgetOffChange = (value, key) => {
    if (value) {
      const fields = form.getFieldsValue()

      fields.applicationItems[key] = {
        ...fields.applicationItems[key],
        estimate: null,
        /*category: null,
        amount: null,
        unit: null,
        name: null,*/
      }

      form.setFieldsValue(fields)
    }
  }

  return (
    <Layout>
      <HeaderNavigation activeKey="1" />
      <Content className={styles.content}>
        <Affix offsetTop={75}>
          <PageHeader
            ghost={false}
            title={currentApplicationItem ? 'Редактировать заявку' : 'Новая заявка'}
            extra={[
              <Button key="1" onClick={() => onCancel()}>
                Отмена
              </Button>,
              <ProtectedAccess allowedRoles={['foreman', 'project_manager']}>
                <Button key="2" type="primary" onClick={() => form.submit()}>
                  {currentApplicationItem ? 'Обновить заявку' : 'Создать заявку'}
                </Button>
              </ProtectedAccess>,
            ]}
          />
        </Affix>
        <div>
          <Spin spinning={isCreating}>
            <Form
              initialValues={initFields}
              name="application"
              form={form}
              onFinish={onFinish}
              className={styles.form}
              layout="vertical"
            >
              <Form.List name="applicationItems">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <div>
                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline" wrap>
                          <Form.Item {...restField} label="Идентификатор" name={[name, 'id']} hidden>
                            <Input />
                          </Form.Item>
                          <Form.Item {...restField} label="Позиция из сметы" name={[name, 'estimate']}>
                            <EstimateItemInput
                              project={currentApplicationItem ? currentApplicationItem?.project?.id : project}
                              onEstimateSelect={(estimate) => onEstimateSelect(estimate, index)}
                              loadOnPress={true}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Категория из сметы"
                            name={[name, 'category']}
                            style={{ maxWidth: 200, minWidth: 200 }}
                          >
                            <Select
                              options={categories.map((category) => ({
                                value: category,
                                label: category,
                              }))}
                              dropdownMatchSelectWidth={false}
                              showSearch
                              allowClear
                            />
                          </Form.Item>
                          <Form.Item {...restField} label="Цена из сметы" name={[name, 'price']}>
                            <DecimalInput style={{ maxWidth: 150, minWidth: 150 }} disabled />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Вне сметы"
                            name={[name, 'off_budget']}
                            style={{ maxWidth: 250, minWidth: 250 }}
                          >
                            <Select
                              options={OFF_BUDGET_OPTIONS}
                              dropdownMatchSelectWidth={false}
                              onChange={(v) => onBudgetOffChange(v, key)}
                              showSearch
                              allowClear
                            />
                          </Form.Item>
                          <Form.Item {...restField} label="Наименование" name={[name, 'name']}>
                            <Input allowClear style={{ maxWidth: 300, minWidth: 300 }} />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Количество"
                            name={[name, 'amount']}
                            rules={[{ required: true, message: 'Поле обязательно' }]}
                          >
                            <DecimalInput style={{ maxWidth: 100, minWidth: 100 }} />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Единица измерения"
                            name={[name, 'unit']}
                            style={{ maxWidth: 200, minWidth: 200 }}
                            rules={[{ required: true, message: 'Поле обязательно' }]}
                          >
                            <SISelect />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, 'description']} label="Примечание">
                            <Input />
                          </Form.Item>
                          {!currentApplicationItem && (
                            <Form.Item label=" ">
                              <MinusCircleOutlined onClick={() => remove(name)} />
                            </Form.Item>
                          )}
                        </Space>
                        <Divider plain />
                      </div>
                    ))}
                    <Form.Item style={{ paddingTop: 15 }}>
                      <Button disabled={!!currentApplicationItem} type="dashed" onClick={() => add()} block>
                        Добавить позицию в заявку
                      </Button>
                    </Form.Item>

                    {currentApplicationItem && currentApplicationItem.estimate_field && (
                      <ProtectedAccess allowedRoles={['project_manager', 'supplier']}>
                        <WebSearchPanel app_item={currentApplicationItem} onUpdate={updateCurrentApplication} />
                      </ProtectedAccess>
                    )}
                  </>
                )}
              </Form.List>
            </Form>
          </Spin>
        </div>
      </Content>
    </Layout>
  )
}

export default NewApplication
